/* Набор иконок (inline SVG, наследуют currentColor). Без внешних зависимостей. */
import type { SVGProps } from 'react'

type IconProps = SVGProps<SVGSVGElement> & { size?: number }

function base(props: IconProps, sw = 1.8) {
  const { size = 18, strokeWidth, ...rest } = props
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: strokeWidth ?? sw,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    ...rest,
  }
}

export function SearchIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  )
}

export function RefreshIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  )
}

export function ChevronDownIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

export function CheckIcon(p: IconProps) {
  return (
    <svg {...base(p, 2.6)} aria-hidden="true">
      <path d="m5 12 5 5L20 7" />
    </svg>
  )
}

export function CloseIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

export function LayersIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="m12 2 9 5-9 5-9-5 9-5Z" />
      <path d="m3 12 9 5 9-5" />
      <path d="m3 17 9 5 9-5" />
    </svg>
  )
}

export function LinkIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M9 17H7A5 5 0 0 1 7 7h2" />
      <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
      <path d="M8 12h8" />
    </svg>
  )
}

export function FlameIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M12 3c0 3-4 4-4 8a4 4 0 0 0 8 0c0-1-.5-2-1-2.5.5 2-1.5 2.5-1.5 2.5C14 9 12 7 12 3Z" />
    </svg>
  )
}

export function ClockIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  )
}

export function LockIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.2" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
    </svg>
  )
}

export function LogoutIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M15 17v1a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1" />
      <path d="M20 12H9m11 0-3-3m3 3-3 3" />
    </svg>
  )
}

export function ActivityIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M3 12h4l2.5-7 5 14L17 12h4" />
    </svg>
  )
}

export function FilterIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M3 5h18l-7 8v6l-4 2v-8L3 5Z" />
    </svg>
  )
}

export function InfoIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </svg>
  )
}

export function AlertIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M12 3 2.5 20h19L12 3Z" />
      <path d="M12 10v4M12 17h.01" />
    </svg>
  )
}

export function BotIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <rect x="5" y="8" width="14" height="11" rx="2.5" />
      <path d="M12 8V4M12 4h-1.5M12 4h1.5" />
      <circle cx="9.5" cy="13" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="14.5" cy="13" r="1.1" fill="currentColor" stroke="none" />
      <path d="M2.5 12v3M21.5 12v3" />
    </svg>
  )
}

export function RadarIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M19.07 4.93A10 10 0 1 0 21 12" />
      <path d="M16 8a6 6 0 1 0 1.5 2.5" />
      <path d="M12 12 19 5" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  )
}

export function CaretIcon({ dir = 'down', ...p }: IconProps & { dir?: 'up' | 'down' }) {
  return (
    <svg {...base({ ...p, size: p.size ?? 12 }, 2.4)} aria-hidden="true">
      {dir === 'up' ? <path d="m6 14 6-6 6 6" /> : <path d="m6 10 6 6 6-6" />}
    </svg>
  )
}

export function PlayIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M7 5v14l11-7L7 5Z" fill="currentColor" stroke="none" />
    </svg>
  )
}

export function StopIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" stroke="none" />
    </svg>
  )
}

export function PowerIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M12 3v9" />
      <path d="M7.5 6.5a7 7 0 1 0 9 0" />
    </svg>
  )
}

export function SettingsIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 13.5a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.9l-.1-.1A2 2 0 1 1 7 3.3l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V2a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H22a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  )
}

export function PlugIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M9 2v6M15 2v6" />
      <path d="M7 8h10v3a5 5 0 0 1-10 0V8Z" />
      <path d="M12 16v6" />
    </svg>
  )
}

export function ListIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  )
}

export function TrashIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />
    </svg>
  )
}

export function ChartIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M4 19V5M4 19h16M8 16v-5M12 16V8M16 16v-3" />
    </svg>
  )
}

export function ShieldIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" />
    </svg>
  )
}

export function ArrowUpIcon(p: IconProps) {
  return (
    <svg {...base(p, 2.4)} aria-hidden="true">
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  )
}

export function ArrowDownIcon(p: IconProps) {
  return (
    <svg {...base(p, 2.4)} aria-hidden="true">
      <path d="M12 5v14M5 12l7 7 7-7" />
    </svg>
  )
}

export function ExternalLinkIcon(p: IconProps) {
  return (
    <svg {...base(p)} aria-hidden="true">
      <path d="M14 4h6v6M20 4l-9 9M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" />
    </svg>
  )
}
